import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Plus, Trash2, CheckCircle2, NotebookPen, Camera, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ALL_SUBJECTS } from "@/lib/constants";

const photoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.storage_path}?token=${p.view_token || ""}`;

export default function WeakTopics({ studentId, readOnly }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ resources_done: 0, units_done: 0 });
  const [cards, setCards] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pform, setPform] = useState({ subject: "", unit: "", note: "" });
  const [pfile, setPfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, c, ph] = await Promise.all([
        api.get(`/analytics/weak-topics/${studentId}`),
        api.get(`/study-cards/${studentId}`),
        api.get(`/weak-photos/${studentId}`),
      ]);
      setItems(w.data.topics || []);
      setSummary({ resources_done: w.data.resources_done || 0, units_done: w.data.units_done || 0 });
      setCards(c.data);
      setPhotos(ph.data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const createCard = async (w) => {
    try {
      await api.post(`/study-cards/${studentId}`, {
        subject: w.subject, unit: w.unit, error_rate: w.error_rate, status: "Açık", note: "",
      });
      toast.success(`Çalışma kartı oluşturuldu: ${w.subject} · ${w.unit}`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleCard = async (c) => {
    try {
      await api.put(`/study-cards/${studentId}/${c.id}`, { ...c, status: c.status === "Bitti" ? "Açık" : "Bitti" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const delCard = async (id) => {
    try {
      await api.delete(`/study-cards/${studentId}/${id}`);
      setCards((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const hasCard = (w) => cards.some((c) => c.subject === w.subject && c.unit === w.unit);

  const uploadPhoto = async (e) => {
    e.preventDefault();
    if (!pfile) return toast.error("Fotoğraf seçin");
    if (!pform.subject) return toast.error("Ders seçin");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pfile);
      fd.append("subject", pform.subject);
      fd.append("unit", pform.unit);
      fd.append("note", pform.note);
      await api.post(`/weak-photos/${studentId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fotoğraflı soru hata defterine eklendi");
      setPform({ subject: "", unit: "", note: "" });
      setPfile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e2) {
      toast.error(apiErr(e2));
    } finally {
      setUploading(false);
    }
  };

  const delPhoto = async (id) => {
    try {
      await api.delete(`/weak-photos/${studentId}/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success("Silindi");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-xl font-bold text-slate-900">Eksik & Yanlışı Çok Olan Konular</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Hata oranı %25 ve üzeri konular, doğru/yanlış sayısına göre kaynak adı ve konu ile otomatik belirlenir.
        </p>
        {!loading && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="weak-summary">
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="weak-res-count">
              {summary.resources_done} kaynak
            </span>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="weak-unit-count">
              {summary.units_done} ünite bitirildi
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="card-ta p-8 text-center text-slate-500">
          <AlertTriangle className="mx-auto mb-2 text-emerald-600" size={28} />
          Kritik hata oranına sahip konu bulunmuyor. Harika gidiyor!
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="weak-topics-list">
          {items.map((w, i) => (
            <div key={i} className="card-ta p-4 border-l-4 border-l-rose-500">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-rose-600 font-semibold">{w.subject}</div>
                  <div className="font-display font-semibold text-slate-900 mt-1">{w.unit}</div>
                  {w.resource_name && (
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1" data-testid={`weak-resource-${i}`}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400"></span>{w.resource_name}
                    </div>
                  )}
                </div>
                <span className="text-lg font-mono font-bold text-rose-600">%{w.error_rate}</span>
              </div>
              <div className="text-xs text-slate-500 mt-3">{w.correct} doğru · {w.wrong} yanlış / {w.total} soru</div>
              {!readOnly && (
                <Button
                  size="sm"
                  disabled={hasCard(w)}
                  onClick={() => createCard(w)}
                  className="mt-3 w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                  data-testid={`weak-card-btn-${i}`}
                >
                  <NotebookPen size={13} /><span className="ml-1">{hasCard(w) ? "Kart Atandı" : "Çalışma Kartı Oluştur"}</span>
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <div>
        <h4 className="font-display text-lg font-bold text-slate-900 mb-3">Özel Çalışma Kartları</h4>
        {cards.length === 0 ? (
          <div className="card-ta p-6 text-center text-slate-500 text-sm">Henüz çalışma kartı yok.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="study-cards-list">
            {cards.map((c) => (
              <div key={c.id} className={`rounded-xl border p-4 ${c.status === "Bitti" ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">{c.subject}</div>
                    <div className="font-semibold text-slate-900 mt-0.5">{c.unit}</div>
                    {c.error_rate != null && <div className="text-[11px] text-slate-500 mt-1">Hata oranı %{c.error_rate}</div>}
                  </div>
                  {!readOnly && <button onClick={() => delCard(c.id)} className="text-slate-300 hover:text-rose-600"><Trash2 size={15} /></button>}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${c.status === "Bitti" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status || "Açık"}</span>
                  {!readOnly && (
                    <button onClick={() => toggleCard(c)} className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid={`card-toggle-${c.id}`}>
                      <CheckCircle2 size={13} /> {c.status === "Bitti" ? "Geri al" : "Bitti işaretle"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hata Defteri — Fotoğraflı Sorular */}
      <div data-testid="error-notebook">
        <h4 className="font-display text-lg font-bold text-slate-900 mb-1">Hata Defteri — Fotoğraflı Sorular</h4>
        <p className="text-sm text-slate-500 mb-3">Yanlış yaptığın soruların fotoğrafını çekip ders/konu ile birlikte ekle.</p>

        {!readOnly && (
          <form onSubmit={uploadPhoto} className="card-ta p-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="ta-label">Ders</label>
                <select className="ta-input" value={pform.subject} onChange={(e) => setPform({ ...pform, subject: e.target.value })} data-testid="weak-photo-subject">
                  <option value="">Seçiniz</option>
                  {ALL_SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="ta-label">Ünite/Konu</label>
                <input className="ta-input" value={pform.unit} onChange={(e) => setPform({ ...pform, unit: e.target.value })} data-testid="weak-photo-unit" />
              </div>
              <div>
                <label className="ta-label">Not</label>
                <input className="ta-input" value={pform.note} onChange={(e) => setPform({ ...pform, note: e.target.value })} data-testid="weak-photo-note" />
              </div>
              <div>
                <label className="ta-label">Fotoğraf</label>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="ta-input text-xs" onChange={(e) => setPfile(e.target.files[0])} data-testid="weak-photo-file" />
              </div>
            </div>
            <Button type="submit" disabled={uploading} className="mt-3 bg-rose-600 hover:bg-rose-700" data-testid="weak-photo-submit">
              {uploading ? <Loader2 className="animate-spin" size={15} /> : <Camera size={15} />}
              <span className="ml-1">Fotoğrafı Ekle</span>
            </Button>
          </form>
        )}

        {photos.length === 0 ? (
          <div className="card-ta p-6 text-center text-slate-500 text-sm">
            <ImagePlus className="mx-auto mb-2 text-slate-300" size={26} />
            Henüz fotoğraflı soru eklenmemiş.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="weak-photos-grid">
            {photos.map((p) => (
              <div key={p.id} className="card-ta overflow-hidden" data-testid={`weak-photo-${p.id}`}>
                <a href={photoUrl(p)} target="_blank" rel="noreferrer">
                  <img src={photoUrl(p)} alt={p.note || p.subject} className="w-full h-36 object-cover bg-slate-100" data-testid={`weak-photo-img-${p.id}`} />
                </a>
                <div className="p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wide text-rose-600 font-bold">{p.subject || "Genel"}</div>
                      <div className="text-xs font-semibold text-slate-800 truncate">{p.unit_name || "-"}</div>
                      {p.note && <div className="text-[11px] text-slate-500 truncate mt-0.5">{p.note}</div>}
                    </div>
                    {!readOnly && (
                      <button onClick={() => delPhoto(p.id)} className="text-slate-300 hover:text-rose-600 shrink-0" data-testid={`weak-photo-del-${p.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
